"use client";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import clsx from "clsx";
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  getYear,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isValid,
  setMonth,
  setYear,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { useState } from "react";
import {
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";
import { LuCalendar } from "react-icons/lu";

const years = Array.from({ length: 50 }, (_, i) => getYear(new Date()) - i);
const months = Array.from({ length: 12 }, (_, i) =>
  format(new Date(2000, i, 1), "MMMM")
);
const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

type View = "year" | "month" | "day";

type Props<T extends FieldValues> = UseControllerProps<T> & {
  label?: string;
  placeholder?: string;
  minDate?: Date | null; // Minimum date (from)
  maxDate?: Date | null; // Maximum date (to)
  isRangeValidation?: boolean; // If true, validates that the date is not less than minDate
  disabled?: boolean;
  noBorder?: boolean;
  noShadow?: boolean;
  className?: string;
  required?: boolean;
};

export function DatePicker<T extends FieldValues>({
  control,
  name,
  rules,
  label,
  placeholder = "Select date",
  minDate = null,
  maxDate = null,
  isRangeValidation = false,
  disabled = false,
  noBorder = false,
  noShadow = false,
  className,
  required = false,
}: Props<T>) {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name, control, rules });

  const [view, setView] = useState<View>("day");
  const [tempDate, setTempDate] = useState<Date>(() => {
    // Initialize with current date if value is null/invalid
    if (value && isValid(new Date(value))) {
      return new Date(value);
    }
    return new Date();
  });
  const [open, setOpen] = useState(false);

  const selectYear = (year: number) => {
    setTempDate(setYear(tempDate, year));
    setView("month");
  };

  const selectMonth = (month: number) => {
    setTempDate(setMonth(tempDate, month));
    setView("day");
  };

  const selectDay = (day: number) => {
    const finalDate = new Date(
      tempDate.getFullYear(),
      tempDate.getMonth(),
      day
    );

    // Range validations
    if (isRangeValidation && minDate && isBefore(finalDate, minDate)) {
      return; // Don't allow dates before minDate
    }

    if (maxDate && isAfter(finalDate, maxDate)) {
      return; // Don't allow dates after maxDate
    }

    onChange(finalDate.toString());
    setOpen(false);
    setView("day");
  };

  const generateDays = () => {
    const start = startOfWeek(startOfMonth(tempDate));
    const end = endOfMonth(tempDate);
    const days: Date[] = [];
    let day = start;
    while (day <= end) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  };

  // Helper function to safely format dates
  const safeFormat = (date: Date, formatString: string) => {
    try {
      return isValid(date) ? format(date, formatString) : "";
    } catch {
      return "";
    }
  };

  // Helper function to check if a date is valid
  const isValidDate = (dateValue: string) => {
    if (!dateValue) return false;
    const date = new Date(dateValue);
    return isValid(date);
  };

  return (
    <div className="space-y-1">
      {label && (
        <label className="block mb-2 text-sm text-gray-600 font-semibold">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <Popover
        open={open && !disabled}
        onOpenChange={(isOpen) => {
          if (!disabled) {
            setOpen(isOpen);
            if (isOpen) {
              // Set tempDate to current value if valid, otherwise use current date
              if (value && isValidDate(value)) {
                setTempDate(new Date(value));
              } else {
                setTempDate(new Date());
              }
              setView("day");
            }
          }
        }}
      >
        <PopoverTrigger asChild>
          <div
            onClick={disabled ? undefined : () => setOpen(true)}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            onKeyDown={disabled ? undefined : (e) => {
              if (e.key === "Enter" || e.key === " ") setOpen(true);
            }}
            className={clsx(
              "block w-full px-4 py-2 mt-2 rounded-md text-sm",
              !noBorder && "border",
              !noShadow && "shadow-sm",
              "focus:outline-none focus:ring focus:ring-brand-400 focus:border-brand-400 focus:ring-opacity-40",
              "flex items-center justify-between",
              error ? "border-red-500" : !noBorder && "border-gray-200",
              disabled ? {
                "bg-gray-100 text-gray-500 cursor-default": true
              } : {
                "hover:border-brand-400 cursor-pointer": true,
                "text-gray-700": value,
                "text-gray-400": !value
              },
              !disabled && !className && "bg-white",
              className
            )}
          >
            {value && isValidDate(value)
              ? safeFormat(new Date(value), "dd/MM/yyyy")
              : placeholder}

            <LuCalendar className="ml-2 h-5 w-5" />
          </div>
        </PopoverTrigger>

        <PopoverContent className="w-[300px] p-4 bg-white" align="start">
          <div className="flex justify-between mb-2">
            <button
              onClick={() => setView("year")}
              className="cursor-pointer text-sm font-semibold hover:underline"
            >
              {safeFormat(tempDate, "MMMM yyyy")}
            </button>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setTempDate(subMonths(tempDate, 1))}
                className="text-gray-500 hover:text-gray-800"
              >
                &lt;
              </button>
              <button
                onClick={() => setTempDate(addMonths(tempDate, 1))}
                className="text-gray-500 hover:text-gray-800"
              >
                &gt;
              </button>
            </div>
          </div>

          {view === "year" && (
            <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto">
              {years.map((year) => (
                <button
                  key={year}
                  onClick={() => selectYear(year)}
                  className="text-sm p-2 rounded hover:bg-gray-100"
                >
                  {year}
                </button>
              ))}
            </div>
          )}

          {view === "month" && (
            <div className="grid grid-cols-3 gap-2">
              {months.map((month, i) => (
                <button
                  key={month}
                  onClick={() => selectMonth(i)}
                  className="text-sm p-2 rounded hover:bg-gray-100"
                >
                  {month}
                </button>
              ))}
            </div>
          )}

          {view === "day" && (
            <>
              <div className="grid grid-cols-7 text-xs text-center mb-1 font-medium text-muted-foreground">
                {weekDays.map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 text-center">
                {generateDays().map((date, idx) => {
                  const disabled =
                    !isSameMonth(date, tempDate) ||
                    (isRangeValidation && minDate && isBefore(date, minDate)) ||
                    (maxDate && isAfter(date, maxDate)) ||
                    false;
                  const isSelected =
                    value &&
                    isValidDate(value) &&
                    isSameDay(date, new Date(value));
                  return (
                    <button
                      key={idx}
                      disabled={disabled}
                      onClick={() => selectDay(date.getDate())}
                      className={clsx(
                        "text-sm p-1 rounded w-8 h-8",
                        disabled
                          ? "text-gray-300 cursor-not-allowed"
                          : isSelected
                          ? "bg-brand-400 text-white"
                          : "hover:bg-gray-100"
                      )}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>

      {error && <p className="text-xs text-red-500">{error.message}</p>}
    </div>
  );
}
